(() => {
  'use strict';

  if (window.__mellerMobileInlineSearchBound) return;
  window.__mellerMobileInlineSearchBound = true;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  let panel = null;
  let lastTrigger = null;
  let opening = false;
  let inlineResultsObserver = null;

  const getModal = () => document.getElementById('search-modal');
  const getSourceInner = () => getModal()?.querySelector('.search-modal__inner');
  const getTriggers = () =>
    Array.from(document.querySelectorAll('[data-meller-search-action] .search-modal__button'));

  const ensurePredictiveSearchDefined = async () => {
    if (customElements.get('predictive-search-component')) return true;

    const sourceScript = document.querySelector('script[src*="predictive-search.js"]');

    if (sourceScript instanceof HTMLScriptElement && sourceScript.src) {
      try {
        await import(sourceScript.src);
      } catch (_error) {
        // The original module may already be loading. Fall through to whenDefined below.
      }
    }

    if (customElements.get('predictive-search-component')) return true;

    await Promise.race([
      customElements.whenDefined('predictive-search-component'),
      new Promise((resolve) => window.setTimeout(resolve, 1500))
    ]);

    return Boolean(customElements.get('predictive-search-component'));
  };

  const ensurePanel = () => {
    if (panel?.isConnected) return panel;

    panel = document.getElementById('meller-mobile-inline-search');

    if (!panel) {
      panel = document.createElement('div');
      panel.id = 'meller-mobile-inline-search';
      panel.className = 'meller-mobile-inline-search search-modal__content';
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
    }

    const headerComponent = document.getElementById('header-component');
    const headerGroup = document.getElementById('header-group');

    if (headerComponent?.parentNode) {
      headerComponent.insertAdjacentElement('afterend', panel);
    } else if (headerGroup) {
      headerGroup.appendChild(panel);
    }

    return panel;
  };

  const setTriggerState = (expanded) => {
    getTriggers().forEach((trigger) => {
      trigger.setAttribute(
        'aria-controls',
        mobileQuery.matches ? 'meller-mobile-inline-search' : 'search-modal'
      );
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  };

  const buildInlineSearch = () => {
    const sourceInner = getSourceInner();
    if (!sourceInner) return null;

    const clone = sourceInner.cloneNode(true);
    clone.classList.add('meller-mobile-inline-search__inner');

    clone.querySelectorAll('script').forEach((script) => script.remove());

    const input = clone.querySelector('.search-input');
    const label = clone.querySelector('label[for]');
    const results = clone.querySelector('[ref="predictiveSearchResults"]');
    const form = clone.querySelector('.predictive-search-form');
    const resetButton = clone.querySelector('[ref="resetButton"]');

    if (input) {
      input.id = 'meller-mobile-inline-search-input';
      input.placeholder = 'Buscar en nuestra tienda';
      input.setAttribute('aria-owns', 'meller-mobile-inline-search-results');
      input.setAttribute('aria-controls', 'meller-mobile-inline-search-results');
      input.removeAttribute('on:input');
      input.removeAttribute('on:keydown');
      input.removeAttribute('on:focus');
    }

    if (label) label.setAttribute('for', 'meller-mobile-inline-search-input');
    if (results) {
      results.id = 'meller-mobile-inline-search-results';
      results.removeAttribute('on:click');
    }

    if (form) form.removeAttribute('on:keydown');
    if (resetButton) resetButton.removeAttribute('on:click');

    clone.querySelector('.predictive-search__close-modal-button')?.remove();

    return clone;
  };

  const bindInlinePredictiveSearch = (searchInner) => {
    const component = searchInner.querySelector('predictive-search-component');
    const input = searchInner.querySelector('.search-input');
    const resetButton = searchInner.querySelector('[ref="resetButton"]');
    const results = searchInner.querySelector('[ref="predictiveSearchResults"]');

    if (!component || !input || !results) return false;

    const syncResultsState = () => {
      const hasResults =
        Boolean(results.querySelector('[data-search-results]')) ||
        results.childElementCount > 0 ||
        results.textContent.trim().length > 0;

      results.classList.toggle('predictive-search-form__content--empty', !hasResults);

      if (hasResults) {
        window.requestAnimationFrame(() => {
          results.scrollTop = 0;
          const inner = results.querySelector('.predictive-search-results__inner');
          if (inner instanceof HTMLElement) inner.scrollTop = 0;
        });
      }
    };

    input.addEventListener('input', (event) => {
      if (typeof component.search === 'function') component.search(event);
      window.requestAnimationFrame(syncResultsState);
    });

    input.addEventListener('focus', () => {
      if (typeof component.expandSearch === 'function') component.expandSearch();
    });

    input.addEventListener('keydown', (event) => {
      if (typeof component.onSearchKeyDown === 'function') component.onSearchKeyDown(event);
    });

    if (resetButton) {
      resetButton.addEventListener('click', () => {
        if (typeof component.resetSearch === 'function') component.resetSearch();
        window.requestAnimationFrame(syncResultsState);
      });
    }

    inlineResultsObserver?.disconnect();
    inlineResultsObserver = new MutationObserver(syncResultsState);
    inlineResultsObserver.observe(results, { childList: true, subtree: true });
    syncResultsState();

    return true;
  };

  const closeInlineSearch = ({ restoreFocus = false } = {}) => {
    const inlinePanel = ensurePanel();

    inlineResultsObserver?.disconnect();
    inlineResultsObserver = null;

    inlinePanel.replaceChildren();
    inlinePanel.hidden = true;
    inlinePanel.setAttribute('aria-hidden', 'true');
    setTriggerState(false);

    if (restoreFocus && lastTrigger instanceof HTMLElement && lastTrigger.isConnected) {
      try {
        lastTrigger.focus({ preventScroll: true });
      } catch (_error) {
        lastTrigger.focus();
      }
    }
  };

  const openInlineSearch = async (trigger) => {
    if (opening) return;
    opening = true;

    try {
      await ensurePredictiveSearchDefined();

      const inlinePanel = ensurePanel();
      const searchInner = buildInlineSearch();
      if (!searchInner) return;

      const component = searchInner.querySelector('predictive-search-component');
      if (component && customElements.get('predictive-search-component')) {
        customElements.upgrade(component);
      }

      lastTrigger = trigger instanceof HTMLElement ? trigger : null;
      inlinePanel.replaceChildren(searchInner);
      bindInlinePredictiveSearch(searchInner);

      inlinePanel.hidden = false;
      inlinePanel.setAttribute('aria-hidden', 'false');
      setTriggerState(true);

      window.requestAnimationFrame(() => {
        const input = inlinePanel.querySelector('.search-input');
        if (!input) return;

        try {
          input.focus({ preventScroll: true });
        } catch (_error) {
          input.focus();
        }
      });
    } finally {
      opening = false;
    }
  };

  const isInlineOpen = () => {
    const inlinePanel = ensurePanel();
    return mobileQuery.matches && !inlinePanel.hidden && inlinePanel.childElementCount > 0;
  };

  const submitFullSearch = (button) => {
    const form = button?.closest('form');
    const input = form?.querySelector('input[name="q"]');

    if (!(input instanceof HTMLInputElement)) return;

    const query = input.value.trim();

    if (!query) {
      try {
        input.focus({ preventScroll: true });
      } catch (_error) {
        input.focus();
      }
      return;
    }

    const searchUrl = new URL(
      form instanceof HTMLFormElement && form.action ? form.action : '/search',
      window.location.origin
    );
    searchUrl.searchParams.set('q', query);

    const prefix = form?.querySelector('input[name="options[prefix]"]');
    if (prefix instanceof HTMLInputElement && prefix.value) {
      searchUrl.searchParams.set('options[prefix]', prefix.value);
    }

    window.location.assign(searchUrl.toString());
  };

  const ensureDrawerAccount = () => {
    const drawer = document.querySelector('.header__drawer--mobile');
    const utilities = drawer?.querySelector('.menu-drawer__utility-links');
    const hasAccountFeature = document.querySelector('header-actions .account-drawer, header-actions account-drawer');

    if (!utilities || !hasAccountFeature || utilities.querySelector('.menu-drawer__account')) return;

    const link = document.createElement('a');
    link.href = '/account';
    link.className = 'menu-drawer__account';
    link.setAttribute('aria-label', 'Cuenta');
    link.innerHTML = '<span class="svg-wrapper" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="8" r="3.25" stroke="currentColor" stroke-width="1.6"/><path d="M5.5 19c.75-3.25 3.2-5 6.5-5s5.75 1.75 6.5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></span><span>Cuenta</span>';
    utilities.prepend(link);
  };

  const setup = () => {
    ensurePanel();
    setTriggerState(false);
    ensureDrawerAccount();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup, { once: true });
  } else {
    setup();
  }

  window.addEventListener(
    'click',
    (event) => {
      if (!mobileQuery.matches) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const trigger = target.closest('[data-meller-search-action] .search-modal__button');

      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (isInlineOpen()) {
          closeInlineSearch({ restoreFocus: true });
        } else {
          void openInlineSearch(trigger);
        }
        return;
      }

      const viewAllButton = target.closest(
        '#meller-mobile-inline-search .predictive-search__search-button'
      );

      if (viewAllButton && isInlineOpen()) {
        event.preventDefault();
        event.stopPropagation();
        submitFullSearch(viewAllButton);
        return;
      }

      if (isInlineOpen() && target.closest('.header__drawer, .header__icon--menu')) {
        closeInlineSearch();
      }
    },
    true
  );

  window.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape' || !isInlineOpen()) return;

      event.preventDefault();
      event.stopPropagation();
      closeInlineSearch({ restoreFocus: true });
    },
    true
  );

  const handleViewportChange = () => {
    if (panel?.isConnected && !panel.hidden) closeInlineSearch();
    setTriggerState(false);
  };

  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', handleViewportChange);
  } else if (typeof mobileQuery.addListener === 'function') {
    mobileQuery.addListener(handleViewportChange);
  }

  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', () => {
      inlineResultsObserver?.disconnect();
      inlineResultsObserver = null;
      panel = null;
      opening = false;
      setup();
    });
  }
})();
